import { DeviceManifest } from '@rapoo/device-sdk'
import {
  gcomeApKeyboardConfig,
  itineraryGcomeAp,
  keyboardMacro,
  keyboardPerfGcomeAp,
  keyFeaturesAdvancedOnly,
} from './generate/capabilityPresets'
import { vt68Keys } from './generate/keysGenerate'
import {
  gcomeApAudioRhythmLightingEffects,
  gcomeApKeyboardLightingEffects,
} from './generate/lightingPresets'

/**
 * VT68 设备清单
 */
const rapooVT68: DeviceManifest = {
  channel: ['china_game'],
  model: 'VT68',
  mainControl: 'VT68',
  type: 'keyboard',
  version: '1.0.0',
  hardware: {
    vendorId: 0x24ae,
    productId: [0x4832],
    currentPid: 0x4832,
    transport: 'HID',
    filters: [
      {
        usagePage: 1,
        usage: 0,
        description: 'Configuration Interface (Secondary)',
        type: 'default',
      },
      {
        // 官方 EXE BusHound：进 Boot 后 USB 重枚举为果来微 Bootloader（非 APP 的 0x24AE:0x4832）
        // Device Descriptor = f5 19 7a af；产品名 Volt67 Upgrader；FF00:1 / 512B Report
        vendorId: 0x19f5,
        productId: 0xaf7a,
        usagePage: 0xff00,
        usage: 0x01,
        description: 'GCOME_AP Bootloader Firmware Update Interface',
        /** 系统选择器常见名（示意，无 VID/PID） */
        productName: 'Volt* Upgrader',
        type: 'update',
      },
    ],
  },
  driver: 'GCOME_AP',
  ui: {
    assetsDir: 'manifests/VT68',
    colors: {
      series: [
        {
          name: 'common.products.VT68.Black',
          value: '#040404',
          key: 'Black',
        },
        {
          name: 'common.products.VT68.Yellow',
          value: '#dfff27',
          key: 'Yellow',
        },
      ],
      current: 'Black',
    },
    icon: 'Hardware-Wireframe-Keyboard',
    overview: true,
  },
  capabilities: [
    gcomeApKeyboardConfig,
    {
      type: 'lighting',
      config: {
        enable: true,
        canAdd: false,
        effects: [...gcomeApKeyboardLightingEffects, ...gcomeApAudioRhythmLightingEffects],
      },
    },
    itineraryGcomeAp,
    keyFeaturesAdvancedOnly,
    keyboardPerfGcomeAp,
    {
      type: 'keys',
      config: vt68Keys,
    },
    keyboardMacro,
  ],
  layout: {
    image: 'frame',
    top: 14,
    bottom: 16,
    left: 16,
    right: 16,
    unit: 40,
    /**
     * key 值为固件 LED 定义区按键索引（0-127）——由 CMD 0x07 GetDefaultKeyMatrix
     * 实测真机（2026-08-17）得到，与物理键位一一对应；0xdd 灯珠顺序即按此索引。
     * ⚠️ 固件矩阵索引按物理矩阵列扫描排列，与视觉行列顺序不同，勿按行序自增。
     *
     * 修饰键为 HID 修饰位图（code0）：
     *   Ctrl_L=34(0x01)  Shift_L=68(0x02)  Alt_L=30(0x04)  Win_L=32(0x08)
     *   Alt_R=9(0x40)    Shift_R=24(0x20)  Fn=7(0xf0/0xff)
     *   本键盘为 68 键布局：无 Ctrl_R。
     */
    keys: [
      [
        {
          key: 51,
          keywords: 'Esc',
        },
        {
          key: 49,
          keywords: '!_1',
        },
        {
          key: 66,
          keywords: '@_2',
        },
        {
          key: 47,
          keywords: '#_3',
        },
        {
          key: 62,
          keywords: '$_4',
        },
        {
          key: 52,
          keywords: '%_5',
        },
        {
          key: 54,
          keywords: '^_6',
        },
        {
          key: 56,
          keywords: '&_7',
        },
        {
          key: 58,
          keywords: '*_8',
        },
        {
          key: 60,
          keywords: '(_9',
        },
        {
          key: 75,
          keywords: ')_0',
        },
        {
          key: 77,
          keywords: '__-',
        },
        {
          key: 79,
          keywords: '+_=',
        },
        {
          key: 46,
          keywords: 'BackSpace',
          w: 2,
        },
        {
          key: 64,
          keywords: 'Insert',
        },
      ],
      [
        {
          key: 53,
          keywords: 'Tab',
          w: 1.5,
        },
        {
          key: 69,
          keywords: 'Q',
        },
        {
          key: 71,
          keywords: 'W',
        },
        {
          key: 89,
          keywords: 'E',
        },
        {
          key: 50,
          keywords: 'R',
        },
        {
          key: 33,
          keywords: 'T',
        },
        {
          key: 31,
          keywords: 'Y',
        },
        {
          key: 29,
          keywords: 'U',
        },
        {
          key: 27,
          keywords: 'I',
        },
        {
          key: 25,
          keywords: 'O',
        },
        {
          key: 23,
          keywords: 'P',
        },
        {
          key: 21,
          keywords: '{_[',
        },
        {
          key: 81,
          keywords: '}_]',
        },
        {
          key: 83,
          keywords: '|_\\',
          w: 1.5,
        },
        {
          key: 85,
          keywords: 'Delete',
        },
      ],
      [
        {
          key: 55,
          keywords: 'CapsLock',
          w: 1.75,
        },
        {
          key: 73,
          keywords: 'A',
        },
        {
          key: 87,
          keywords: 'S',
        },
        {
          key: 48,
          keywords: 'D',
        },
        {
          key: 35,
          keywords: 'F',
        },
        {
          key: 16,
          keywords: 'G',
        },
        {
          key: 12,
          keywords: 'H',
        },
        {
          key: 10,
          keywords: 'J',
        },
        {
          key: 8,
          keywords: 'K',
        },
        {
          key: 6,
          keywords: 'L',
        },
        {
          key: 4,
          keywords: ':_;',
        },
        {
          key: 2,
          keywords: '"_\'',
        },
        {
          key: 0,
          keywords: 'Enter',
          w: 2.25,
        },
        {
          key: 39,
          keywords: 'PgUp',
        },
      ],
      [
        {
          key: 68,
          keywords: 'Shift_L',
          w: 2.25,
        },
        {
          key: 38,
          keywords: 'Z',
        },
        {
          key: 36,
          keywords: 'X',
        },
        {
          key: 37,
          keywords: 'C',
        },
        {
          key: 18,
          keywords: 'V',
        },
        {
          key: 13,
          keywords: 'B',
        },
        {
          key: 15,
          keywords: 'N',
        },
        {
          key: 17,
          keywords: 'M',
        },
        {
          key: 19,
          keywords: '<_,',
        },
        {
          key: 20,
          keywords: '>_.',
        },
        {
          key: 22,
          keywords: '?_/',
        },
        {
          key: 24,
          keywords: 'Shift_R',
          w: 1.75,
        },
        {
          key: 26,
          keywords: '↑',
        },
        {
          key: 28,
          keywords: 'PgDn',
        },
      ],
      [
        {
          key: 34,
          keywords: 'Ctrl_L',
          w: 1.25,
        },
        {
          key: 32,
          keywords: 'Win_L',
          w: 1.25,
        },
        {
          key: 30,
          keywords: 'Alt_L',
          w: 1.25,
        },
        {
          key: 11,
          keywords: 'Space_L',
          w: 6.25,
          /** 空格长键额外 4 个 LED 灯珠（真机扫描 2026-08-17）：主灯 11 + 73/75/77/79 共 5 灯 */
          ledIndexes: [72, 74, 76, 78],
        },
        {
          key: 9,
          keywords: 'Alt_R',
          w: 1.25,
        },
        {
          key: 7,
          keywords: 'Fn',
          w: 1.25,
        },
        {
          key: 5,
          keywords: '←',
          x: 0.5,
        },
        {
          key: 3,
          keywords: '↓',
        },
        {
          key: 1,
          keywords: '→',
        },
      ],
    ],
  },
}

export default rapooVT68
